import { useId, useMemo, useState } from 'react';
import type { PointerEvent, ReactNode } from 'react';
import { DataTable, type Spalte } from './DataTable';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { LoadingState } from './LoadingState';
import { formatiereZahl } from './WertEinheit';
import {
  MONATE, MONATE_LANG, REIHEN_HOECHSTENS, STRICHARTEN, achsenteilung, bandpfad, bereich, istZahl,
  jeMonat, lastdauer, linienpfad, runde, skala, stundeImJahr, verdichte, verdichteAufSpalten,
} from './diagramm';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIAGRAMME (TX3, TX-STANDARD S1.8, S1.10 Nr. 3)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `Liniendiagramm`, `Balkendiagramm`, `Lastdauerlinie`, `Jahresgang` - SVG
 * ohne Bibliothek. Für alle gilt:
 *
 * - Reihenfarben aus `--diagramm-1…8` in FESTER Reihenfolge (Reihe 1 Blau),
 *   dazu eine Strichart je Reihe; höchstens 8 Reihen. Zustandsfarben nur für
 *   bewertete Werte (`grenzen`: Grenzlinie mit Titel).
 * - Achsen beschriftet, mit Einheit; Werte deutsch (`formatiereZahl`).
 * - `role="img"` mit Titel und Kurzbeschreibung; darunter immer der
 *   Ausklapper „Werte als Tabelle" (`DataTable`, mit CSV) - die Tabelle trägt
 *   alle ECHTEN Werte, auch wenn das Bild verdichtet (Jahresgang 8760 h).
 * - Lade-, Fehler- und fachlicher Leerzustand wie jede Fläche (S1.4).
 * - Beim Zeigen mit der Maus eine Ablesehilfe (Wert je Reihe); die Werte
 *   stehen aber nie nur dort (S1.10 Nr. 9).
 * - Fehlstellen werden nicht überbrückt (Lücke in der Linie, kein Balken).
 */

export interface Reihe {
  schluessel: string;
  titel: string;
  werte: (number | null)[];
}

/** Bewertete Grenze, z. B. „Anschlussleistung 900 kW" - in Zustandsfarbe und mit Text. */
export interface Grenze {
  wert: number;
  titel: string;
  zustand?: 'fehler' | 'warnung' | 'info' | 'gut';
}

export interface DiagrammBasis {
  /** Überschrift (h3) und Name des Bildes. */
  titel: string;
  /** Ein Satz, was das Bild zeigt - für Vorlesehilfen und unter dem Titel. */
  beschreibung?: string;
  /** Einheit der Werteachse, z. B. „kW". */
  einheit: string;
  /** Beschriftung der Werteachse, z. B. „Kühllast". */
  achsenTitel?: string;
  nachkomma?: number;
  /** Werteachse bei 0 beginnen (Vorgabe `true`; Temperaturen: `false`). */
  nullEinschliessen?: boolean;
  grenzen?: Grenze[];
  laedt?: boolean;
  fehler?: unknown;
  beiErneutVersuchen?: () => void;
  leer?: ReactNode;
  /** Höhe der Zeichenfläche in px (Vorgabe 240). */
  hoehe?: number;
  /** Ausklapper „Werte als Tabelle" offen. */
  tabelleOffen?: boolean;
  className?: string;
}

/* ═══ Zeichenfläche ═══════════════════════════════════════════════════════ */

const BREITE = 720;
const RAND = { links: 64, rechts: 16, oben: 12, unten: 40 };

function Rahmen({ titel, beschreibung, kinder, tabelle, tabelleOffen, legende, className }: {
  titel: string; beschreibung?: string; kinder: ReactNode; tabelle?: ReactNode; tabelleOffen?: boolean;
  legende?: ReactNode; className?: string;
}) {
  return (
    <figure className={`m-diagramm${className ? ` ${className}` : ''}`}>
      <figcaption className="m-diagramm-kopf">
        <h3 className="m-diagramm-titel">{titel}</h3>
        {beschreibung && <p className="m-diagramm-beschreibung">{beschreibung}</p>}
      </figcaption>
      {legende}
      {kinder}
      {tabelle && (
        <details className="m-diagramm-werte" open={tabelleOffen}>
          <summary>Werte als Tabelle</summary>
          {tabelle}
        </details>
      )}
    </figure>
  );
}

function Zustaende({ basis, leerText }: { basis: DiagrammBasis; leerText: string }) {
  if (basis.fehler !== undefined && basis.fehler !== null) {
    return (
      <Rahmen titel={basis.titel} className={basis.className} kinder={(
        <ErrorState meldung="Das Diagramm konnte nicht aufgebaut werden." technisch={basis.fehler}
          beiErneutVersuchen={basis.beiErneutVersuchen} klein />
      )} />
    );
  }
  if (basis.laedt) {
    return <Rahmen titel={basis.titel} className={basis.className} kinder={<LoadingState text={`${basis.titel} wird geladen …`} />} />;
  }
  return (
    <Rahmen titel={basis.titel} className={basis.className} kinder={basis.leer ?? (
      <EmptyState titel={leerText} klein
        erklaerung="Sobald gerechnet wurde, steht hier das Diagramm mit seinen Werten." />
    )} />
  );
}

function Legende({ reihen }: { reihen: { titel: string }[] }) {
  if (reihen.length < 2) return null;
  return (
    <ul className="m-diagramm-legende">
      {reihen.map((r, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={i}>
          <svg width="24" height="10" aria-hidden="true" className="m-diagramm-legendenzeichen">
            <line x1="0" y1="5" x2="24" y2="5" className={`m-reihe-strich m-reihe-${i + 1}`}
              strokeDasharray={STRICHARTEN[i] || undefined} />
          </svg>
          {r.titel}
        </li>
      ))}
    </ul>
  );
}

/** Werteachse mit Raster und Beschriftung; dazu die Grenzlinien. */
function Werteachse({ marken, y, einheit, titel, nachkomma, hoehe, grenzen }: {
  marken: number[]; y: (w: number) => number; einheit: string; titel?: string; nachkomma?: number;
  hoehe: number; grenzen?: Grenze[];
}) {
  return (
    <g className="m-diagramm-achse">
      {marken.map((m) => (
        <g key={m}>
          <line x1={RAND.links} x2={BREITE - RAND.rechts} y1={runde(y(m))} y2={runde(y(m))}
            className="m-diagramm-raster" />
          <text x={RAND.links - 6} y={runde(y(m))} dy="0.32em" textAnchor="end" className="m-diagramm-marke">
            {formatiereZahl(m, nachkomma ?? (Number.isInteger(m) ? 0 : undefined))}
          </text>
        </g>
      ))}
      <text x={12} y={hoehe / 2} textAnchor="middle" className="m-diagramm-achsentitel"
        transform={`rotate(-90 12 ${hoehe / 2})`}>
        {titel ? `${titel} [${einheit}]` : `[${einheit}]`}
      </text>
      {(grenzen ?? []).map((g) => (
        <g key={g.titel} className={`m-diagramm-grenze m-diagramm-grenze-${g.zustand ?? 'fehler'}`}>
          <line x1={RAND.links} x2={BREITE - RAND.rechts} y1={runde(y(g.wert))} y2={runde(y(g.wert))} />
          <text x={BREITE - RAND.rechts - 4} y={runde(y(g.wert)) - 4} textAnchor="end">
            {g.titel}: {formatiereZahl(g.wert, nachkomma)} {einheit}
          </text>
        </g>
      ))}
    </g>
  );
}

/** Ablesehilfe: nächster Index zur Mausposition (Bildkoordinate → Index). */
function useAblesen(anzahl: number, xVonIndex: (i: number) => number) {
  const [index, setIndex] = useState<number | null>(null);
  const beiZeigen = (e: PointerEvent<SVGSVGElement>) => {
    const svg = e.currentTarget;
    const r = svg.getBoundingClientRect();
    if (r.width === 0 || anzahl === 0) return;
    const bx = ((e.clientX - r.left) / r.width) * BREITE;
    let best = 0;
    let abstand = Infinity;
    for (let i = 0; i < anzahl; i += 1) {
      const d = Math.abs(xVonIndex(i) - bx);
      if (d < abstand) { abstand = d; best = i; }
    }
    setIndex(best);
  };
  return { index, beiZeigen, beiVerlassen: () => setIndex(null) };
}

function Ablesung({ x, zeilen, hoehe }: { x: number; zeilen: string[]; hoehe: number }) {
  const breite = 180;
  const links = x + 8 + breite > BREITE ? x - 8 - breite : x + 8;
  return (
    <g className="m-diagramm-ablesung" aria-hidden="true">
      <line x1={x} x2={x} y1={RAND.oben} y2={hoehe - RAND.unten} />
      <rect x={links} y={RAND.oben} width={breite} height={14 + zeilen.length * 14} rx={4} />
      {zeilen.map((t, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <text key={i} x={links + 8} y={RAND.oben + 16 + i * 14}>{t}</text>
      ))}
    </g>
  );
}

/* ═══ Liniendiagramm ══════════════════════════════════════════════════════ */

export interface LiniendiagrammProps extends DiagrammBasis {
  /** Beschriftung je Punkt der Rechtsachse (Monat, Stunde, Lastfall …). */
  x: string[];
  xTitel: string;
  reihen: Reihe[];
}

export function Liniendiagramm(p: LiniendiagrammProps) {
  const id = useId();
  const hoehe = p.hoehe ?? 240;
  const reihen = p.reihen.slice(0, REIHEN_HOECHSTENS);
  // Leer ist ein Diagramm ohne einen Datenwert - eine Grenzlinie allein ist kein Inhalt.
  const daten = bereich(reihen.map((r) => r.werte));
  const b = bereich([...reihen.map((r) => r.werte), (p.grenzen ?? []).map((g) => g.wert)]);
  const ach = achsenteilung(b?.min ?? 0, b?.max ?? 1, { nullEinschliessen: p.nullEinschliessen ?? true });
  const y = skala(ach.von, ach.bis, hoehe - RAND.unten, RAND.oben);
  const n = p.x.length;
  const x = (i: number) => (n <= 1 ? (RAND.links + BREITE - RAND.rechts) / 2
    : RAND.links + (i / (n - 1)) * (BREITE - RAND.links - RAND.rechts));
  const ablesen = useAblesen(n, x);

  if (!daten || p.laedt || (p.fehler !== undefined && p.fehler !== null)) {
    return <Zustaende basis={p} leerText="Noch keine Werte für dieses Diagramm." />;
  }

  const schritt = Math.max(1, Math.ceil(n / 12));
  return (
    <Rahmen
      titel={p.titel} beschreibung={p.beschreibung} className={p.className} tabelleOffen={p.tabelleOffen}
      legende={<Legende reihen={reihen} />}
      kinder={(
        <svg viewBox={`0 0 ${BREITE} ${hoehe}`} width="100%" role="img" aria-labelledby={`${id}-t ${id}-b`}
          className="m-diagramm-flaeche" onPointerMove={ablesen.beiZeigen} onPointerLeave={ablesen.beiVerlassen}>
          <title id={`${id}-t`}>{p.titel}</title>
          <desc id={`${id}-b`}>{p.beschreibung ?? `${reihen.map((r) => r.titel).join(', ')} über ${p.xTitel}; Werte in der Tabelle darunter.`}</desc>
          <Werteachse marken={ach.marken} y={y} einheit={p.einheit} titel={p.achsenTitel} nachkomma={p.nachkomma}
            hoehe={hoehe} grenzen={p.grenzen} />
          <g className="m-diagramm-rechtsachse">
            {p.x.map((t, i) => (i % schritt === 0 ? (
              // eslint-disable-next-line react/no-array-index-key
              <text key={i} x={runde(x(i))} y={hoehe - RAND.unten + 16} textAnchor="middle" className="m-diagramm-marke">{t}</text>
            ) : null))}
            <text x={(RAND.links + BREITE - RAND.rechts) / 2} y={hoehe - 4} textAnchor="middle" className="m-diagramm-achsentitel">
              {p.xTitel}
            </text>
          </g>
          {reihen.map((r, i) => (
            <path key={r.schluessel} d={linienpfad(r.werte, x, y)} className={`m-reihe-strich m-reihe-${i + 1}`}
              strokeDasharray={STRICHARTEN[i] || undefined} data-reihe={r.schluessel} />
          ))}
          {ablesen.index !== null && (
            <Ablesung x={runde(x(ablesen.index))} hoehe={hoehe} zeilen={[
              p.x[ablesen.index],
              ...reihen.map((r) => `${r.titel}: ${formatiereZahl(r.werte[ablesen.index!], p.nachkomma)} ${p.einheit}`),
            ]} />
          )}
        </svg>
      )}
      tabelle={<Wertetabelle titel={p.titel} xTitel={p.xTitel} x={p.x} reihen={reihen} einheit={p.einheit} nachkomma={p.nachkomma} />}
    />
  );
}

/* ═══ Balkendiagramm ══════════════════════════════════════════════════════ */

export interface BalkendiagrammProps extends DiagrammBasis {
  kategorien: string[];
  xTitel: string;
  reihen: Reihe[];
  /** Reihen übereinander statt nebeneinander (nur für Teile eines Ganzen). */
  gestapelt?: boolean;
}

export function Balkendiagramm(p: BalkendiagrammProps) {
  const id = useId();
  const hoehe = p.hoehe ?? 240;
  const reihen = p.reihen.slice(0, REIHEN_HOECHSTENS);
  const n = p.kategorien.length;
  const summen = p.kategorien.map((_, k) => reihen.reduce((s, r) => s + (istZahl(r.werte[k]) ? r.werte[k]! : 0), 0));
  const daten = bereich(reihen.map((r) => r.werte));
  const b = bereich([...(p.gestapelt ? [summen] : []), ...reihen.map((r) => r.werte),
    (p.grenzen ?? []).map((g) => g.wert)]);
  const ach = achsenteilung(Math.min(b?.min ?? 0, 0), b?.max ?? 1, { nullEinschliessen: true });
  const y = skala(ach.von, ach.bis, hoehe - RAND.unten, RAND.oben);
  const flaeche = BREITE - RAND.links - RAND.rechts;
  const feld = n > 0 ? flaeche / n : flaeche;
  const mitte = (k: number) => RAND.links + feld * (k + 0.5);
  const ablesen = useAblesen(n, mitte);

  if (!daten || p.laedt || (p.fehler !== undefined && p.fehler !== null)) {
    return <Zustaende basis={p} leerText="Noch keine Werte für dieses Diagramm." />;
  }

  // 2 px Lücke zwischen Balken (Fläche), schmale Balken: höchstens 40 px.
  const innen = Math.min(feld * 0.7, p.gestapelt ? 40 : 40 * reihen.length);
  const balken = p.gestapelt ? innen : innen / Math.max(1, reihen.length);
  const null0 = y(0);

  return (
    <Rahmen
      titel={p.titel} beschreibung={p.beschreibung} className={p.className} tabelleOffen={p.tabelleOffen}
      legende={<Legende reihen={reihen} />}
      kinder={(
        <svg viewBox={`0 0 ${BREITE} ${hoehe}`} width="100%" role="img" aria-labelledby={`${id}-t ${id}-b`}
          className="m-diagramm-flaeche" onPointerMove={ablesen.beiZeigen} onPointerLeave={ablesen.beiVerlassen}>
          <title id={`${id}-t`}>{p.titel}</title>
          <desc id={`${id}-b`}>{p.beschreibung ?? `${reihen.map((r) => r.titel).join(', ')} je ${p.xTitel}; Werte in der Tabelle darunter.`}</desc>
          <Werteachse marken={ach.marken} y={y} einheit={p.einheit} titel={p.achsenTitel} nachkomma={p.nachkomma}
            hoehe={hoehe} grenzen={p.grenzen} />
          <g className="m-diagramm-rechtsachse">
            {p.kategorien.map((t, k) => (
              // eslint-disable-next-line react/no-array-index-key
              <text key={k} x={runde(mitte(k))} y={hoehe - RAND.unten + 16} textAnchor="middle" className="m-diagramm-marke">{t}</text>
            ))}
            <text x={(RAND.links + BREITE - RAND.rechts) / 2} y={hoehe - 4} textAnchor="middle" className="m-diagramm-achsentitel">
              {p.xTitel}
            </text>
          </g>
          {p.kategorien.map((t, k) => {
            let stapel = 0;
            return (
              // eslint-disable-next-line react/no-array-index-key
              <g key={k} data-kategorie={t}>
                {reihen.map((r, i) => {
                  const w = r.werte[k];
                  if (!istZahl(w)) return null;
                  const unten = p.gestapelt ? stapel : 0;
                  const oben = unten + w;
                  if (p.gestapelt) stapel = oben;
                  const yo = y(Math.max(unten, oben));
                  const yu = y(Math.min(unten, oben));
                  const xl = p.gestapelt ? mitte(k) - balken / 2 : mitte(k) - innen / 2 + i * balken;
                  return (
                    <rect key={r.schluessel} x={runde(xl + 1)} width={runde(Math.max(1, balken - 2))}
                      y={runde(yo)} height={runde(Math.max(0, yu - yo - (p.gestapelt ? 2 : 0)))}
                      className={`m-reihe-flaeche m-reihe-${i + 1}`} data-reihe={r.schluessel} />
                  );
                })}
              </g>
            );
          })}
          <line x1={RAND.links} x2={BREITE - RAND.rechts} y1={runde(null0)} y2={runde(null0)} className="m-diagramm-nulllinie" />
          {ablesen.index !== null && (
            <Ablesung x={runde(mitte(ablesen.index))} hoehe={hoehe} zeilen={[
              p.kategorien[ablesen.index],
              ...reihen.map((r) => `${r.titel}: ${formatiereZahl(r.werte[ablesen.index!], p.nachkomma)} ${p.einheit}`),
            ]} />
          )}
        </svg>
      )}
      tabelle={<Wertetabelle titel={p.titel} xTitel={p.xTitel} x={p.kategorien} reihen={reihen} einheit={p.einheit}
        nachkomma={p.nachkomma} summe={p.gestapelt} />}
    />
  );
}

/* ═══ Lastdauerlinie ══════════════════════════════════════════════════════ */

export interface LastdauerlinieProps extends DiagrammBasis {
  /** Stundenwerte (unsortiert, z. B. 8760); Fehlstellen fallen weg und werden genannt. */
  werte: (number | null)[];
  /** Zeitbezeichnung je Wert für die Wertetabelle (Vorgabe: Stunde des Jahres). */
  zeitpunkt?: (index: number) => string;
  /** Schwellen, für die „Stunden über" ausgewiesen werden (z. B. Grundlastkessel). */
  schwellen?: number[];
}

export function Lastdauerlinie(p: LastdauerlinieProps) {
  const id = useId();
  const hoehe = p.hoehe ?? 240;
  const ld = useMemo(() => lastdauer(p.werte), [p.werte]);
  const n = ld.geordnet.length;
  const ach = achsenteilung(0, Math.max(ld.hoechstwert ?? 1, ...(p.grenzen ?? []).map((g) => g.wert)), { nullEinschliessen: true });
  const xa = achsenteilung(0, Math.max(1, n), { nullEinschliessen: true, ziel: 6 });
  const y = skala(ach.von, ach.bis, hoehe - RAND.unten, RAND.oben);
  const xs = skala(0, xa.bis, RAND.links, BREITE - RAND.rechts);
  // Verdichtet auf die Bildspalten: Hülle (Max je Spalte) als Linie - eine
  // geordnete Reihe ist monoton, Max und Min einer Spalte liegen beieinander.
  const spalten = useMemo(() => verdichteAufSpalten(ld.geordnet, BREITE - RAND.links - RAND.rechts), [ld.geordnet]);
  const ablesen = useAblesen(spalten.length, (i) => xs(spalten[i].von));

  if (n === 0 || p.laedt || (p.fehler !== undefined && p.fehler !== null)) {
    return <Zustaende basis={p} leerText="Noch keine Stundenwerte für eine Lastdauerlinie." />;
  }

  const pfad = linienpfad(spalten.map((s) => s.max), (i) => xs(spalten[i].von), y)
    + (n > 0 ? `L${runde(xs(n))},${runde(y(ld.geordnet[n - 1]))}` : '');
  const flaeche = `${pfad}L${runde(xs(n))},${runde(y(0))}L${runde(xs(0))},${runde(y(0))}Z`;

  const kennwerte = [
    { kennwert: 'Höchstwert', wert: ld.hoechstwert, einheit: p.einheit },
    { kennwert: 'Volllaststunden (Σ / Höchstwert)', wert: ld.volllaststunden, einheit: 'h' },
    ...(p.schwellen ?? []).map((s) => ({
      kennwert: `Stunden über ${formatiereZahl(s, p.nachkomma)} ${p.einheit}`, wert: ld.stundenUeber(s), einheit: 'h',
    })),
    { kennwert: 'Stunden ohne Wert', wert: ld.fehlend, einheit: 'h' },
  ];

  return (
    <Rahmen
      titel={p.titel} className={p.className} tabelleOffen={p.tabelleOffen}
      beschreibung={p.beschreibung ?? `Höchstwert ${formatiereZahl(ld.hoechstwert, p.nachkomma)} ${p.einheit}, ${
        formatiereZahl(ld.volllaststunden, 0)} Volllaststunden${ld.fehlend > 0 ? `, ${formatiereZahl(ld.fehlend)} Stunden ohne Wert` : ''}.`}
      kinder={(
        <svg viewBox={`0 0 ${BREITE} ${hoehe}`} width="100%" role="img" aria-labelledby={`${id}-t ${id}-b`}
          className="m-diagramm-flaeche" onPointerMove={ablesen.beiZeigen} onPointerLeave={ablesen.beiVerlassen}>
          <title id={`${id}-t`}>{p.titel}</title>
          <desc id={`${id}-b`}>Die Werte des Jahres absteigend geordnet; Kennwerte und alle Werte in der Tabelle darunter.</desc>
          <Werteachse marken={ach.marken} y={y} einheit={p.einheit} titel={p.achsenTitel} nachkomma={p.nachkomma}
            hoehe={hoehe} grenzen={p.grenzen} />
          <g className="m-diagramm-rechtsachse">
            {xa.marken.map((m) => (
              <text key={m} x={runde(xs(m))} y={hoehe - RAND.unten + 16} textAnchor="middle" className="m-diagramm-marke">
                {formatiereZahl(m, 0)}
              </text>
            ))}
            <text x={(RAND.links + BREITE - RAND.rechts) / 2} y={hoehe - 4} textAnchor="middle" className="m-diagramm-achsentitel">
              Dauer [h]
            </text>
          </g>
          <path d={flaeche} className="m-reihe-flaeche m-reihe-1 m-diagramm-unterflaeche" />
          <path d={pfad} className="m-reihe-strich m-reihe-1" data-reihe="lastdauer" />
          {ablesen.index !== null && (
            <Ablesung x={runde(xs(spalten[ablesen.index].von))} hoehe={hoehe} zeilen={[
              `${formatiereZahl(spalten[ablesen.index].von, 0)} h`,
              `${formatiereZahl(spalten[ablesen.index].max, p.nachkomma)} ${p.einheit}`,
            ]} />
          )}
        </svg>
      )}
      tabelle={(
        <>
          <DataTable
            name="Kennwerte"
            spalten={[
              { schluessel: 'kennwert', titel: 'Kennwert', wert: (z) => z.kennwert },
              { schluessel: 'wert', titel: 'Wert', art: 'zahl', nachkomma: p.nachkomma, wert: (z) => z.wert },
              { schluessel: 'einheit', titel: 'Einheit', wert: (z) => z.einheit },
            ]}
            zeilen={kennwerte}
            schluessel={(z) => z.kennwert}
            suche={false}
            spaltenwahl={false}
          />
          <Stundentabelle titel={p.titel} werte={p.werte} einheit={p.einheit} nachkomma={p.nachkomma}
            zeitpunkt={p.zeitpunkt} />
        </>
      )}
    />
  );
}

/* ═══ Jahresgang ══════════════════════════════════════════════════════════ */

export interface JahresgangProps extends DiagrammBasis {
  /**
   * 12 Monatswerte ODER Stundenwerte eines Jahres (8760, im Schaltjahr 8784).
   * Stundenwerte werden zur DARSTELLUNG je Tag verdichtet (Band Min-Max,
   * Linie Mittel); die Wertetabelle zeigt die echten Stundenwerte.
   */
  werte: (number | null)[];
  /** Zweite Reihe (z. B. Außentemperatur) - nur bei 12 Monatswerten, eigene Einheit wäre ein zweites Diagramm. */
  vergleich?: Reihe;
  zeitpunkt?: (index: number) => string;
}

export function Jahresgang(p: JahresgangProps) {
  const id = useId();
  const hoehe = p.hoehe ?? 240;
  const stunden = p.werte.length > 12;
  const schaltjahr = p.werte.length === 8784;
  const tage = useMemo(() => (stunden ? verdichte(p.werte, 24) : []), [p.werte, stunden]);
  const monate = useMemo(() => (stunden ? jeMonat(p.werte, schaltjahr) : []), [p.werte, stunden, schaltjahr]);

  if (!stunden) {
    // 12 Monatswerte: Balken je Monat - dasselbe Muster wie Balkendiagramm.
    return (
      <Balkendiagramm
        {...p}
        kategorien={[...MONATE]}
        xTitel="Monat"
        reihen={[{ schluessel: 'wert', titel: p.achsenTitel ?? p.titel, werte: p.werte }, ...(p.vergleich ? [p.vergleich] : [])]}
      />
    );
  }

  const daten = bereich([tage.map((t) => t.min), tage.map((t) => t.max)]);
  const b = bereich([tage.map((t) => t.min), tage.map((t) => t.max), (p.grenzen ?? []).map((g) => g.wert)]);
  if (!daten || !b || p.laedt || (p.fehler !== undefined && p.fehler !== null)) {
    return <Zustaende basis={p} leerText="Noch keine Stundenwerte für den Jahresgang." />;
  }
  const ach = achsenteilung(b.min, b.max, { nullEinschliessen: p.nullEinschliessen ?? true });
  const y = skala(ach.von, ach.bis, hoehe - RAND.unten, RAND.oben);
  const x = (tag: number) => RAND.links + ((tag + 0.5) / tage.length) * (BREITE - RAND.links - RAND.rechts);
  const mtage = monate.map((m) => m.von / 24);

  return (
    <JahresgangBild
      id={id} p={p} hoehe={hoehe} y={y} x={x} marken={ach.marken} tage={tage} monatsanfang={mtage}
      tabelle={(
        <>
          <DataTable
            name="Monatswerte"
            spalten={[
              { schluessel: 'monat', titel: 'Monat', wert: (z) => z.monat, sortierbar: false },
              { schluessel: 'min', titel: 'Minimum', art: 'zahl', einheit: p.einheit, nachkomma: p.nachkomma, wert: (z) => z.min },
              { schluessel: 'mittel', titel: 'Mittel', art: 'zahl', einheit: p.einheit, nachkomma: p.nachkomma, wert: (z) => z.mittel },
              { schluessel: 'max', titel: 'Maximum', art: 'zahl', einheit: p.einheit, nachkomma: p.nachkomma, wert: (z) => z.max },
              { schluessel: 'summe', titel: 'Summe', art: 'zahl', einheit: `${p.einheit}h`, nachkomma: 0, wert: (z) => z.summe, fuss: 'summe' },
              { schluessel: 'fehlend', titel: 'Stunden ohne Wert', art: 'zahl', nachkomma: 0, wert: (z) => z.fehlend, fuss: 'summe' },
            ]}
            zeilen={monate.map((m, i) => ({ ...m, monat: MONATE_LANG[i] }))}
            schluessel={(z) => z.monat}
            suche={false}
            ausfuhr
          />
          <Stundentabelle titel={p.titel} werte={p.werte} einheit={p.einheit} nachkomma={p.nachkomma}
            zeitpunkt={p.zeitpunkt ?? ((h) => {
              const s = stundeImJahr(h, schaltjahr);
              return `${String(s.tag).padStart(2, '0')}.${String(s.monat + 1).padStart(2, '0')}. ${String(s.stunde).padStart(2, '0')}:00`;
            })} />
        </>
      )}
    />
  );
}

function JahresgangBild({ id, p, hoehe, y, x, marken, tage, monatsanfang, tabelle }: {
  id: string; p: JahresgangProps; hoehe: number; y: (w: number) => number; x: (tag: number) => number;
  marken: number[]; tage: ReturnType<typeof verdichte>; monatsanfang: number[]; tabelle: ReactNode;
}) {
  const ablesen = useAblesen(tage.length, x);
  const band = bandpfad(tage, x, y);
  const mittel = linienpfad(tage.map((t) => t.mittel), x, y);
  return (
    <Rahmen
      titel={p.titel} className={p.className} tabelleOffen={p.tabelleOffen}
      beschreibung={p.beschreibung ?? `Je Tag Spanne (Minimum bis Maximum) und Mittel aus ${formatiereZahl(p.werte.length)} Stundenwerten.`}
      legende={(
        <ul className="m-diagramm-legende">
          <li><span className="m-diagramm-bandzeichen m-reihe-1" aria-hidden="true" /> Spanne je Tag (Minimum bis Maximum)</li>
          <li>
            <svg width="24" height="10" aria-hidden="true"><line x1="0" y1="5" x2="24" y2="5" className="m-reihe-strich m-reihe-1" /></svg>
            Mittel je Tag
          </li>
        </ul>
      )}
      kinder={(
        <svg viewBox={`0 0 ${BREITE} ${hoehe}`} width="100%" role="img" aria-labelledby={`${id}-t ${id}-b`}
          className="m-diagramm-flaeche" onPointerMove={ablesen.beiZeigen} onPointerLeave={ablesen.beiVerlassen}>
          <title id={`${id}-t`}>{p.titel}</title>
          <desc id={`${id}-b`}>Jahresgang aus Stundenwerten, je Tag verdichtet; Monats- und alle Stundenwerte in der Tabelle darunter.</desc>
          <Werteachse marken={marken} y={y} einheit={p.einheit} titel={p.achsenTitel} nachkomma={p.nachkomma}
            hoehe={hoehe} grenzen={p.grenzen} />
          <g className="m-diagramm-rechtsachse">
            {monatsanfang.map((t, i) => (
              <text key={MONATE[i]} x={runde(x(t + 14))} y={hoehe - RAND.unten + 16} textAnchor="middle" className="m-diagramm-marke">
                {MONATE[i]}
              </text>
            ))}
          </g>
          <path d={band} className="m-reihe-flaeche m-reihe-1 m-diagramm-band" data-reihe="spanne" />
          <path d={mittel} className="m-reihe-strich m-reihe-1" data-reihe="mittel" />
          {ablesen.index !== null && (
            <Ablesung x={runde(x(ablesen.index))} hoehe={hoehe} zeilen={[
              `Tag ${ablesen.index + 1}`,
              `Min ${formatiereZahl(tage[ablesen.index].min, p.nachkomma)} ${p.einheit}`,
              `Mittel ${formatiereZahl(tage[ablesen.index].mittel, p.nachkomma)} ${p.einheit}`,
              `Max ${formatiereZahl(tage[ablesen.index].max, p.nachkomma)} ${p.einheit}`,
            ]} />
          )}
        </svg>
      )}
      tabelle={tabelle}
    />
  );
}

/* ═══ Wertetabellen ═══════════════════════════════════════════════════════ */

function Wertetabelle({ titel, xTitel, x, reihen, einheit, nachkomma, summe }: {
  titel: string; xTitel: string; x: string[]; reihen: Reihe[]; einheit: string; nachkomma?: number; summe?: boolean;
}) {
  const zeilen = x.map((t, i) => ({ id: `${i}`, x: t, i }));
  const spalten: Spalte<{ id: string; x: string; i: number }>[] = [
    { schluessel: 'x', titel: xTitel, wert: (z) => z.x, sortierbar: false },
    ...reihen.map((r): Spalte<{ id: string; x: string; i: number }> => ({
      schluessel: r.schluessel, titel: r.titel, art: 'zahl', einheit, nachkomma,
      wert: (z) => (istZahl(r.werte[z.i]) ? r.werte[z.i] : null), fuss: summe ? 'summe' : undefined,
    })),
  ];
  return (
    <DataTable name={`${titel} - Werte`} spalten={spalten} zeilen={zeilen} schluessel={(z) => z.id}
      suche={false} ausfuhr={zeilen.length > 0} />
  );
}

interface Stundenzeile { id: string; i: number; w: number | null }

/**
 * Alle echten Stundenwerte (Auflage main: nicht verdichtet). `DataTable`
 * virtualisiert, die CSV enthält jeden Wert - das ist die Excel-taugliche
 * Fassung derselben Zahlen.
 */
function Stundentabelle({ titel, werte, einheit, nachkomma, zeitpunkt }: {
  titel: string; werte: (number | null)[]; einheit: string; nachkomma?: number; zeitpunkt?: (i: number) => string;
}) {
  const zeilen = useMemo(() => werte.map((w, i): Stundenzeile => ({ id: `${i}`, i, w: istZahl(w) ? w : null })), [werte]);
  const spalten: Spalte<Stundenzeile>[] = [
    { schluessel: 'i', titel: 'Stunde', art: 'zahl', nachkomma: 0, wert: (z) => z.i + 1 },
    ...(zeitpunkt ? [{ schluessel: 'zeit', titel: 'Zeitpunkt', wert: (z: Stundenzeile) => zeitpunkt(z.i), sortierbar: false }] : []),
    { schluessel: 'w', titel: 'Wert', art: 'zahl', einheit, nachkomma, wert: (z) => z.w },
  ];
  return (
    <DataTable
      name={`${titel} - alle ${formatiereZahl(werte.length)} Werte`}
      spalten={spalten}
      zeilen={zeilen}
      schluessel={(z) => z.id}
      ausfuhr
      hoehe={320}
    />
  );
}
